import { EditorStarterItem } from '../items/item-type';
import type { TransitionItem } from '../items/transition/transition-item-type';
import { TrackType } from '../state/types';

/**
 * Link된 클립을 찾기 위한 유틸리티 함수들
 *
 * Linking이 활성화되면 video, caption, audio main track이 연동될 때,
 * 같은 타임라인 위치(from, durationInFrames)를 가진 클립들을 찾아서 함께 작업합니다.
 */

/** Link 대상 트랙 타입들 */
const LINKABLE_TRACK_TYPES = ['video', 'caption', 'audio'] as const;
type LinkableTrackType = (typeof LINKABLE_TRACK_TYPES)[number];

interface LinkedItemInfo {
  itemId: string;
  /** 타임라인상 시작 위치 (프레임) */
  from: number;
  /** 타임라인상 길이 (프레임) */
  durationInFrames: number;
  trackId: string;
  type: string;
}

/**
 * 아이템에서 타임라인상 위치 (from, durationInFrames)를 추출합니다.
 */
export const getItemTimelineRange = (
  item: EditorStarterItem,
): { from: number; durationInFrames: number } | null => {
  const from = (item as { from?: number }).from;
  const durationInFrames = (item as { durationInFrames?: number })
    .durationInFrames;

  if (typeof from === 'number' && typeof durationInFrames === 'number') {
    return {
      from,
      durationInFrames,
    };
  }

  return null;
};

/**
 * 아이템에서 renderMeta의 videoStart/videoEnd를 추출합니다.
 * (캡션 확장 등 다른 용도로 사용)
 */
export const getItemVideoRange = (
  item: EditorStarterItem,
): { videoStart: number; videoEnd: number; linkId?: number } | null => {
  const renderMeta = (
    item as {
      renderMeta?: { videoStart?: number; videoEnd?: number; linkId?: number };
    }
  ).renderMeta;

  if (
    renderMeta &&
    typeof renderMeta.videoStart === 'number' &&
    typeof renderMeta.videoEnd === 'number'
  ) {
    return {
      videoStart: renderMeta.videoStart,
      videoEnd: renderMeta.videoEnd,
      linkId: renderMeta.linkId,
    };
  }

  return null;
};

/**
 * 트랙 타입이 Link 대상인지 확인합니다.
 */
const isLinkableTrackType = (
  trackType: string | undefined,
): trackType is LinkableTrackType => {
  return LINKABLE_TRACK_TYPES.includes(trackType as LinkableTrackType);
};

/**
 * 모든 트랙에서 Link 가능한 아이템 정보를 수집합니다.
 * (video, caption, audio main track의 아이템들)
 * 타임라인상 위치 (from, durationInFrames) 기준으로 수집합니다.
 */
export const collectLinkedItemsInfo = ({
  tracks,
  items,
}: {
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
}): LinkedItemInfo[] => {
  const linkedItems: LinkedItemInfo[] = [];

  for (const track of tracks) {
    // main track의 video, caption, audio 트랙만 대상
    if (!track.metadata?.isMainTrack || track.locked) {
      continue;
    }

    const trackType = track.metadata?.type;
    if (!isLinkableTrackType(trackType)) {
      continue;
    }

    for (const itemId of track.items) {
      const item = items[itemId];
      if (!item) continue;

      const timelineRange = getItemTimelineRange(item);
      if (timelineRange) {
        linkedItems.push({
          itemId,
          from: timelineRange.from,
          durationInFrames: timelineRange.durationInFrames,
          trackId: track.id,
          type: item.type,
        });
      }
    }
  }

  return linkedItems;
};

/**
 * 주어진 아이템의 범위와 연결된 아이템들을 찾습니다.
 *
 * 매칭 규칙:
 * 0. 정확히 일치 (MTS용): from과 durationInFrames가 동일한 모든 아이템
 *    → video, caption, audio 등 모든 linkable 트랙의 아이템
 *
 * 1. 정확히 일치 (LTS용): Video.start == Caption.start AND Video.end == Caption.end
 *    → Video + 해당 Caption (1:1)
 *
 * 2. 범위 매칭: Video.start에 시작이 일치하는 Caption + Video.end에 끝이 일치하는 Caption 존재
 *    → Video + 시작Caption + 끝Caption + 사이의 모든 Caption
 *
 * 3. Caption 삭제 시:
 *    - Caption과 정확히 일치하는 Video 있음 → 규칙1 적용
 *    - 없으면 Caption을 포함하는 Video 찾아서 → 규칙2 적용
 *
 * @param targetItemId - 기준이 되는 아이템 ID
 * @param tracks - 모든 트랙 정보
 * @param items - 모든 아이템 정보
 * @returns 연결된 아이템 ID 배열 (자기 자신 포함)
 */
export const findLinkedItemsByVideoRange = ({
  targetItemId,
  tracks,
  items,
}: {
  targetItemId: string;
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
}): string[] => {
  const targetItem = items[targetItemId];
  if (!targetItem) {
    return [targetItemId];
  }

  // target item이 linkable track에 있는지 확인
  // linkable track에 없으면 linking 대상이 아님 (자기 자신만 반환)
  const targetTrack = tracks.find((track) =>
    track.items.includes(targetItemId),
  );
  if (
    !targetTrack ||
    targetTrack.locked ||
    !isLinkableTrack(targetTrack) ||
    targetTrack.metadata?.type !== 'video'
  ) {
    return [targetItemId];
  }

  const targetRange = getItemTimelineRange(targetItem);
  if (!targetRange) {
    return [targetItemId];
  }

  const allLinkedItems = collectLinkedItemsInfo({ tracks, items });
  const linkedItemIds: string[] = [targetItemId];

  const targetStart = targetRange.from;
  const targetEnd = targetRange.from + targetRange.durationInFrames;

  // 규칙 0: 정확히 같은 from, durationInFrames를 가진 모든 아이템 찾기
  // (MTS에서 video, caption, audio가 같은 scene에 속한 경우)
  const exactMatchItems = allLinkedItems.filter((info) => {
    if (info.itemId === targetItemId) return false;
    return (
      info.from === targetStart &&
      info.from + info.durationInFrames === targetEnd
    );
  });

  // 정확히 일치하는 아이템들 추가
  for (const info of exactMatchItems) {
    if (!linkedItemIds.includes(info.itemId)) {
      linkedItemIds.push(info.itemId);
    }
  }

  // 정확히 일치하는 아이템이 있으면 바로 반환
  if (exactMatchItems.length > 0) {
    return linkedItemIds;
  }

  // 아래는 LTS용 범위 매칭 로직 (caption이 여러 개로 분할된 경우)
  if (targetItem.type === 'video' || targetItem.type === 'image') {
    // Video 클립인 경우
    const videoStart = targetStart;
    const videoEnd = targetEnd;

    // 규칙 2: Video.start에 시작이 일치하는 Caption + Video.end에 끝이 일치하는 Caption 찾기
    const startMatchCaption = allLinkedItems.find((info) => {
      if (info.type !== 'captions') return false;
      return info.from === videoStart;
    });

    const endMatchCaption = allLinkedItems.find((info) => {
      if (info.type !== 'captions') return false;
      const captionEnd = info.from + info.durationInFrames;
      return captionEnd === videoEnd;
    });

    if (startMatchCaption && endMatchCaption) {
      // 시작/끝 일치하는 Caption이 모두 있으면, 그 사이의 모든 Caption 포함
      for (const info of allLinkedItems) {
        if (info.itemId === targetItemId) continue;
        if (info.type !== 'captions') continue;

        const captionStart = info.from;
        const captionEnd = info.from + info.durationInFrames;

        // Video 범위 안에 있는 Caption (시작과 끝이 Video 범위 내에 있음)
        if (captionStart >= videoStart && captionEnd <= videoEnd) {
          if (!linkedItemIds.includes(info.itemId)) {
            linkedItemIds.push(info.itemId);
          }
        }
      }
    }
  } else if (targetItem.type === 'captions') {
    // Caption 클립인 경우
    // Caption 삭제 시에는 delete-items.ts에서 split 로직으로 처리하므로,
    // 여기서는 자기 자신만 반환 (연결된 아이템은 delete-items에서 split 후 찾음)
    // 이 함수는 주로 "이동" 시 연결된 아이템을 찾는 용도로 사용됨
    // Caption 이동 시에는 정확히 일치하는 아이템만 함께 이동해야 함 (규칙 0에서 이미 처리)
  }

  return linkedItemIds;
};

/**
 * 여러 아이템에 대해 연결된 모든 아이템들을 찾습니다.
 *
 * @param targetItemIds - 기준이 되는 아이템 ID 배열
 * @param tracks - 모든 트랙 정보
 * @param items - 모든 아이템 정보
 * @returns 연결된 모든 아이템 ID 배열 (중복 제거)
 */
export const findAllLinkedItems = ({
  targetItemIds,
  tracks,
  items,
}: {
  targetItemIds: string[];
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
}): string[] => {
  const allLinkedItemIds = new Set<string>();

  for (const itemId of targetItemIds) {
    const linkedIds = findLinkedItemsByVideoRange({
      targetItemId: itemId,
      tracks,
      items,
    });

    for (const id of linkedIds) {
      allLinkedItemIds.add(id);
    }
  }

  return Array.from(allLinkedItemIds);
};

/**
 * 주어진 트랙이 Link 대상 트랙인지 확인합니다.
 * (video, caption, audio main track)
 */
export const isLinkableTrack = (track: TrackType): boolean => {
  if (!track.metadata?.isMainTrack) {
    return false;
  }

  const trackType = track.metadata?.type;
  return isLinkableTrackType(trackType);
};

/**
 * Link 대상 트랙들을 찾습니다.
 */
export const findLinkableTracks = (tracks: TrackType[]): TrackType[] => {
  return tracks.filter(isLinkableTrack);
};

/**
 * 트랜지션으로 연결된 아이템들을 찾습니다.
 * 트랜지션은 sourceItemId와 targetItemId로 두 아이템을 연결합니다.
 * 체인으로 연결된 모든 아이템을 재귀적으로 찾습니다. (A-T-B-T-C 형태)
 *
 * @param targetItemId - 기준이 되는 아이템 ID
 * @param transitions - 모든 트랜지션 정보
 * @returns 트랜지션으로 연결된 모든 아이템 ID 배열 (자기 자신 포함)
 */
export const findTransitionLinkedItems = ({
  targetItemId,
  transitions,
}: {
  targetItemId: string;
  transitions: Record<string, TransitionItem>;
}): string[] => {
  const linkedItemIds = new Set<string>([targetItemId]);
  const visited = new Set<string>([targetItemId]);
  const queue = [targetItemId];

  // BFS로 트랜지션 체인을 따라 연결된 모든 아이템 찾기
  while (queue.length > 0) {
    const currentItemId = queue.shift()!;

    for (const transition of Object.values(transitions)) {
      // 현재 아이템이 트랜지션의 source인 경우 -> target 추가
      if (transition.sourceItemId === currentItemId) {
        if (!visited.has(transition.targetItemId)) {
          visited.add(transition.targetItemId);
          linkedItemIds.add(transition.targetItemId);
          queue.push(transition.targetItemId);
        }
      }

      // 현재 아이템이 트랜지션의 target인 경우 -> source 추가
      if (transition.targetItemId === currentItemId) {
        if (!visited.has(transition.sourceItemId)) {
          visited.add(transition.sourceItemId);
          linkedItemIds.add(transition.sourceItemId);
          queue.push(transition.sourceItemId);
        }
      }
    }
  }

  return Array.from(linkedItemIds);
};

/**
 * 여러 아이템에 대해 트랜지션으로 연결된 모든 아이템들을 찾습니다.
 *
 * @param targetItemIds - 기준이 되는 아이템 ID 배열
 * @param transitions - 모든 트랜지션 정보
 * @returns 트랜지션으로 연결된 모든 아이템 ID 배열 (중복 제거)
 */
export const findAllTransitionLinkedItems = ({
  targetItemIds,
  transitions,
}: {
  targetItemIds: string[];
  transitions: Record<string, TransitionItem>;
}): string[] => {
  const allLinkedItemIds = new Set<string>();

  for (const itemId of targetItemIds) {
    const linkedIds = findTransitionLinkedItems({
      targetItemId: itemId,
      transitions,
    });

    for (const id of linkedIds) {
      allLinkedItemIds.add(id);
    }
  }

  return Array.from(allLinkedItemIds);
};

/**
 * 주어진 아이템들과 연결된 트랜지션들을 찾습니다.
 * (이동 시 사용 - source와 target 모두 이동 대상에 포함된 경우에만)
 *
 * @param itemIds - 아이템 ID 배열
 * @param transitions - 모든 트랜지션 정보
 * @returns 연결된 트랜지션 ID 배열
 */
export const findTransitionsForItems = ({
  itemIds,
  transitions,
}: {
  itemIds: string[];
  transitions: Record<string, TransitionItem>;
}): string[] => {
  const transitionIds: string[] = [];
  const itemIdSet = new Set(itemIds);

  for (const [transitionId, transition] of Object.entries(transitions)) {
    // source와 target 모두 이동 대상에 포함된 경우에만 트랜지션도 함께 이동
    if (
      itemIdSet.has(transition.sourceItemId) &&
      itemIdSet.has(transition.targetItemId)
    ) {
      transitionIds.push(transitionId);
    }
  }

  return transitionIds;
};

/**
 * 삭제될 아이템들과 연결된 고아 트랜지션들을 찾습니다.
 * (삭제 시 사용 - source 또는 target 중 하나라도 삭제 대상에 포함된 경우)
 *
 * @param itemIds - 삭제할 아이템 ID 배열
 * @param transitions - 모든 트랜지션 정보
 * @returns 고아가 될 트랜지션 ID 배열
 */
export const findOrphanedTransitions = ({
  itemIds,
  transitions,
}: {
  itemIds: string[];
  transitions: Record<string, TransitionItem>;
}): string[] => {
  const transitionIds: string[] = [];
  const itemIdSet = new Set(itemIds);

  for (const [transitionId, transition] of Object.entries(transitions)) {
    // source 또는 target 중 하나라도 삭제 대상에 포함된 경우 트랜지션도 삭제
    if (
      itemIdSet.has(transition.sourceItemId) ||
      itemIdSet.has(transition.targetItemId)
    ) {
      transitionIds.push(transitionId);
    }
  }

  return transitionIds;
};
